import { useEffect, useState } from 'react'
import { ChevronRight, Wrench } from 'lucide-react'

import { Badge, RiskBadge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils/cn'
import { formatMs } from '@/lib/utils/format'
import type { ToolActivity } from '@/types/events'

const STATUS_TEXT: Record<ToolActivity['status'], string> = {
  pending: 'selected',
  running: 'running',
  success: 'done',
  failed: 'failed',
  denied: 'blocked by policy',
}

const STATUS_MARK: Record<ToolActivity['status'], string> = {
  pending: 'bg-ink-faint',
  running: 'bg-primary animate-pulse',
  success: 'bg-ok',
  failed: 'bg-fail',
  denied: 'bg-denied',
}

/** Ticks while a call is in flight, so a slow tool looks busy rather than stuck. */
function ElapsedTimer({ since }: { since: number }) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const timer = window.setInterval(() => setElapsed(Date.now() - since), 100)
    return () => window.clearInterval(timer)
  }, [since])
  return <span className="mono text-ink-muted">{formatMs(elapsed)}</span>
}

function ArgumentList({ args }: { args: Record<string, unknown> }) {
  const entries = Object.entries(args).filter(([, value]) => value !== null && value !== undefined)
  if (entries.length === 0) {
    return <p className="text-xs text-ink-faint">No arguments.</p>
  }
  return (
    <dl className="grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="mono truncate text-ink-muted">{key}</dt>
          <dd className="mono break-words text-ink-dim">
            {Array.isArray(value) ? value.join(', ') : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * One tool call in a conversation.
 *
 * Collapsed by default: the header answers "which tool, did it work, how long" — which is
 * what a reader wants nine times out of ten — and expanding reveals the arguments, the
 * upstream status and the request id. Showing all of that inline would bury the answer the
 * user actually asked for.
 */
export function ToolCallCard({
  activity,
  scaleMs,
  children,
}: {
  activity: ToolActivity
  scaleMs: number
  children?: React.ReactNode
}) {
  // Open by default when something needs attention. A failure the reader has to click to
  // see is a failure they will miss.
  const [open, setOpen] = useState(
    activity.status === 'failed' || activity.status === 'denied',
  )
  const isProblem = activity.status === 'failed'
  const width = activity.latencyMs
    ? Math.min(100, Math.max((activity.latencyMs / Math.max(scaleMs, 1)) * 100, 2))
    : 0

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border bg-raised/50',
        isProblem ? 'border-fail/30' : 'border-line',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors
          hover:bg-hover"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-ink-faint transition-transform',
            open && 'rotate-90',
          )}
        />
        <span className="relative flex h-5 w-5 shrink-0 items-center justify-center rounded
          bg-surface text-ink-muted ring-1 ring-line" aria-hidden="true">
          <Wrench className="h-3 w-3" />
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-2 ring-surface',
              STATUS_MARK[activity.status],
            )}
          />
        </span>
        <span className="mono min-w-0 flex-1 truncate font-medium text-ink">{activity.tool}</span>
        <RiskBadge risk={activity.risk} />
        <span className="hidden text-xs text-ink-muted sm:inline">
          {STATUS_TEXT[activity.status]}
        </span>
        {activity.status === 'running' ? (
          <ElapsedTimer since={activity.startedAt} />
        ) : activity.latencyMs !== undefined ? (
          <span className="mono w-16 text-right text-ink-dim">{formatMs(activity.latencyMs)}</span>
        ) : (
          <span className="w-16" />
        )}
      </button>

      {activity.latencyMs !== undefined ? (
        <div className="mx-3 mb-2 h-0.5 overflow-hidden rounded-sm bg-line">
          <div
            className={cn(
              'h-full origin-left animate-grow',
              isProblem ? 'bg-fail' : 'bg-line-strong',
            )}
            style={{ width: `${width}%` }}
          />
        </div>
      ) : null}

      {open ? (
        <div className="space-y-3 border-t border-line bg-surface px-3 py-2.5">
          <ArgumentList args={activity.arguments} />

          {activity.resultSummary ? (
            <p className="text-xs text-ink-dim">{activity.resultSummary}</p>
          ) : null}

          {activity.errorType ? (
            <div className="space-y-1">
              <Badge tone="fail">{activity.errorType}</Badge>
              <p className="text-xs text-ink-dim">{activity.errorMessage}</p>
            </div>
          ) : null}

          {activity.requestId ? (
            <p className="mono text-ink-faint">request {activity.requestId}</p>
          ) : null}
        </div>
      ) : null}

      {children}
    </div>
  )
}
